import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../styles/Specials.css';
import UserPersona from '../components/UserPersona';

// Import images directly
import seafoodFlyer from '../assets/images/specials/Black-Bold-Best-Seafood-Flyer-Instagram-Post.png';
import seafoodMenu from '../assets/images/specials/Seafood-menu.png';
import sundaySlugger from '../assets/images/specials/sunday-slugger.png';
import saturdaySpecial from '../assets/images/specials/Grand-Slam-Saturdays-Instagram-Post.png';
import ladiesNight from '../assets/images/specials/Black-and-Pink-Wine-Illustration-Ladies-Night-Poster.png';
import draftBeer from '../assets/images/specials/draft.png';
import wingsWednesday from '../assets/images/specials/Wildcard-Wing-Wednesday-TCW.png';

const Specials = () => {
  // Create a mapping of image filenames to imported images
  const imageMap = {
    'Black_Bold_Best_Seafood_Flyer_Instagram_Post.png': seafoodFlyer,
    'Seafood_menu.png': seafoodMenu,
    'sunday_slugger.png': sundaySlugger,
    'Grand_Slam_Saturdays_Instagram_Post.png': saturdaySpecial,
    'Black_and_Pink_Wine_Illustration_Ladies_Night_Poster.png': ladiesNight,
    'draft.png': draftBeer,
    'Wildcard_Wing_Wednesday_TCW.png': wingsWednesday
  };

  const [dailySpecials, setDailySpecials] = useState([
    {
      id: 1,
      day: 'Monday',
      title: 'Monday Night Football Special',
      description: 'Enjoy our $2 draft beers while watching the game!',
      price: '$2',
      image: 'draft.png'
    },
    {
      id: 2,
      day: 'Tuesday',
      title: 'Taco Tuesday',
      description: 'Enjoy our special taco offerings every Tuesday!',
      price: 'Special Pricing',
      image: 'draft.png'
    },
    {
      id: 3,
      day: 'Wednesday',
      title: 'Wing Wednesday',
      description: 'Get a dozen wings at a special price every Wednesday!',
      price: '$13.50',
      image: 'Wildcard_Wing_Wednesday_TCW.png'
    },
    {
      id: 4,
      day: 'Thursday',
      title: 'Chicks and Sips',
      description: 'Ladies Night! Enjoy special drink prices and appetizers.',
      price: 'Special Pricing',
      image: 'Black_and_Pink_Wine_Illustration_Ladies_Night_Poster.png'
    },
    {
      id: 5,
      day: 'Friday',
      title: 'Friday Night Flights',
      description: 'Try our beer flight specials to kick off your weekend!',
      price: 'Special Pricing',
      image: 'Grand_Slam_Saturdays_Instagram_Post.png'
    },
    {
      id: 6,
      day: 'Saturday',
      title: 'Blitz Bites Saturday Special',
      description: 'Enjoy our special Saturday appetizer platters!',
      price: '$35',
      image: 'Grand_Slam_Saturdays_Instagram_Post.png'
    },
    {
      id: 7,
      day: 'Sunday',
      title: 'Sunday Showdown Special',
      description: 'Slugger Special - Perfect for game day!',
      price: 'Special Pricing',
      image: 'sunday_slugger.png'
    }
  ]);

  const [limitedSpecials, setLimitedSpecials] = useState([
    {
      id: 1,
      title: 'Boiled Seafood Platters',
      description: 'Try our delicious seafood platters while they last!',
      image: 'Black_Bold_Best_Seafood_Flyer_Instagram_Post.png'
    },
    {
      id: 2,
      title: 'Limited Time Seafood Menu',
      description: 'By popular demand, we\'re bringing back some of the seafood favorites from the 242 Pub days!',
      image: 'Seafood_menu.png'
    }
  ]);

  const [userPersonas, setUserPersonas] = useState([
    {
      id: 1,
      name: 'Mike Thompson',
      image: 'https://randomuser.me/api/portraits/men/32.jpg',
      quote: 'Wing Wednesday is the highlight of my week! My buddies and I never miss it.',
      favorite: 'Wing Wednesday'
    },
    {
      id: 2,
      name: 'Sarah Johnson',
      image: 'https://randomuser.me/api/portraits/women/44.jpg',
      quote: 'Ladies Night is perfect for our girls\' night out. Great drinks and even better company!',
      favorite: 'Chicks and Sips (Thursday)'
    },
    {
      id: 3,
      name: 'David Rodriguez',
      image: 'https://randomuser.me/api/portraits/men/67.jpg',
      quote: 'The seafood platters are amazing! Reminds me of home-cooked meals from my childhood.',
      favorite: 'Boiled Seafood Platters'
    }
  ]);

  const [isLoading, setIsLoading] = useState(false);
  const specialsRef = useRef([]);
  const limitedRef = useRef([]);

  // Animation on scroll effect
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const handleIntersect = (entries, observer) => {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting) {
          // Set a custom property for staggered animations
          entry.target.style.setProperty('--card-index', entry.target.dataset.index);
          entry.target.classList.add('animate');
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);

    // Observe special cards
    specialsRef.current.forEach((card, index) => {
      if (card) {
        card.dataset.index = index;
        observer.observe(card);
      }
    });

    // Observe limited special cards
    limitedRef.current.forEach((card, index) => {
      if (card) {
        card.dataset.index = index;
        observer.observe(card);
      }
    });

    return () => {
      specialsRef.current.forEach(card => {
        if (card) observer.unobserve(card);
      });
      limitedRef.current.forEach(card => {
        if (card) observer.unobserve(card);
      });
    };
  }, []);

  return (
    <div className="specials-page">
      {/* Hero Section */}
      <section className="hero-section specials-hero">
        <div className="hero-content">
          <h1>Daily Specials</h1>
          <p>We're serving up new promos and specials on our delicious menu items all the time!</p>
        </div>
      </section>

      {/* Intro Section */}
      <section className="specials-intro">
        <div className="container">
          <h2>Feeling Special? Treat Yo' Self!</h2>
          <p className="subtitle">Our daily specials are available for dine-in only</p>
        </div>
      </section>

      {/* Daily Specials */}
      <section className="daily-specials">
        <div className="container">
          <h2>Daily Specials</h2>
          <div className="specials-grid">
            {dailySpecials.map((special, index) => (
              <div 
                key={special.id} 
                className="special-card"
                ref={el => specialsRef.current[index] = el}
              >
                <div className="special-day">{special.day}</div>
                <div className="special-image">
                  <img 
                    src={imageMap[special.image]} 
                    alt={`${special.day} Special`} 
                  />
                </div>
                <div className="special-content">
                  <h3>{special.title}</h3>
                  <p className="special-description">{special.description}</p>
                  <p className="special-price">{special.price}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* User Personas Section */}
      <section className="user-personas">
        <div className="container">
          <h2>What Our Customers Say</h2>
          <div className="personas-container">
            {userPersonas.map((persona, index) => (
              <div key={persona.id} className={`persona-wrapper ${index % 2 === 0 ? 'slide-in-left' : 'slide-in-right'}`}>
                <UserPersona
                  name={persona.name}
                  image={persona.image}
                  quote={persona.quote}
                  favorite={persona.favorite}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Limited Time Specials */}
      <section className="limited-time-specials">
        <div className="container">
          <h2>Limited Time Offers</h2>
          <div className="limited-specials-grid">
            {limitedSpecials.map((special, index) => (
              <div 
                key={special.id} 
                className="limited-special-card"
                ref={el => limitedRef.current[index] = el}
              >
                <div className="limited-special-image">
                  <img 
                    src={imageMap[special.image]} 
                    alt={special.title} 
                  />
                </div>
                <div className="limited-special-content">
                  <h3>{special.title}</h3>
                  <p className="limited-special-description">{special.description}</p>
                  <Link to="/menu" className="btn">View Menu</Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="specials-cta">
        <div className="container">
          <h2>Don't Miss Out!</h2>
          <p>Visit us today to enjoy these amazing specials. Dine-in only.</p>
          <Link to="/locations" className="btn">Find a Location</Link>
        </div>
      </section>
    </div>
  );
};

export default Specials;
